import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useState, useEffect, useCallback, useRef } from 'react';
import apiClient from '../services/api-client';

// Types
interface Notification {
  id: string;
  organization_id: string;
  type: 'alert' | 'warning' | 'info' | 'success' | 'error';
  category: 'contract' | 'budget' | 'work_order' | 'equipment' | 'stock' | 'purchase' | 'preventive_maintenance' | 'system' | 'other';
  title: string;
  message: string;
  priority: 'low' | 'medium' | 'high' | 'urgent';
  entity_type?: string;
  entity_id?: string;
  entity_code?: string;
  action_url?: string;
  action_label?: string;
  recipient_type: 'user' | 'role' | 'department' | 'all';
  recipient_id?: string;
  delivery_channels: Array<'in_app' | 'email' | 'sms'>;
  status: 'pending' | 'sent' | 'read' | 'dismissed' | 'expired';
  sent_at?: string;
  read_at?: string;
  dismissed_at?: string;
  expires_at?: string;
  metadata?: Record<string, any>;
  created_at: string;
  created_by?: string;
  recipient_status?: string;
  recipient_read_at?: Date;
}

interface NotificationPreferences {
  id: string;
  user_id: string;
  organization_id: string;
  contract_alerts: boolean;
  budget_alerts: boolean;
  work_order_alerts: boolean;
  equipment_alerts: boolean;
  stock_alerts: boolean;
  purchase_alerts: boolean;
  preventive_maintenance_alerts: boolean;
  system_alerts: boolean;
  in_app_enabled: boolean;
  email_enabled: boolean;
  sms_enabled: boolean;
  quiet_hours_enabled: boolean;
  quiet_hours_start?: string;
  quiet_hours_end?: string;
  daily_digest_enabled: boolean;
  weekly_digest_enabled: boolean;
  created_at: string;
  updated_at: string;
}

interface NotificationListParams {
  status?: string;
  category?: string;
  limit?: number;
  offset?: number;
  unreadOnly?: boolean;
}

interface NotificationListResponse {
  items: Notification[];
  total: number;
}

// Query keys
export const notificationKeys = {
  all: ['notifications'] as const,
  lists: () => [...notificationKeys.all, 'list'] as const,
  list: (filters: NotificationListParams) => [...notificationKeys.lists(), filters] as const,
  unreadCount: () => [...notificationKeys.all, 'unread-count'] as const,
  preferences: () => [...notificationKeys.all, 'preferences'] as const,
};

// Hooks
export function useNotifications(params: NotificationListParams = {}) {
  return useQuery({
    queryKey: notificationKeys.list(params),
    queryFn: async () => {
      const searchParams = new URLSearchParams();

      if (params.status) searchParams.set('status', params.status);
      if (params.category) searchParams.set('category', params.category);
      if (params.limit) searchParams.set('limit', params.limit.toString());
      if (params.offset) searchParams.set('offset', params.offset.toString());
      if (params.unreadOnly) searchParams.set('unread_only', 'true');

      const response = await apiClient.get<NotificationListResponse>(
        `/notifications?${searchParams.toString()}`
      );
      return response.data;
    },
    refetchInterval: 10000, // Refetch every 10 seconds
    staleTime: 5000,
  });
}

export function useUnreadCount() {
  return useQuery({
    queryKey: notificationKeys.unreadCount(),
    queryFn: async () => {
      const response = await apiClient.get<{ count: number }>(
        '/notifications/unread-count'
      );
      return response.data.count;
    },
    refetchInterval: 10000, // Refetch every 10 seconds
    staleTime: 5000,
  });
}

export function useNotificationPreferences() {
  return useQuery({
    queryKey: notificationKeys.preferences(),
    queryFn: async () => {
      const response = await apiClient.get<{ preferences: NotificationPreferences }>(
        '/notifications/preferences'
      );
      return response.data.preferences;
    },
  });
}

export function useMarkAsRead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient.post(`/notifications/${id}/read`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: notificationKeys.lists() });
      queryClient.invalidateQueries({ queryKey: notificationKeys.unreadCount() });
    },
  });
}

export function useMarkAllAsRead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      await apiClient.post('/notifications/read-all');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: notificationKeys.lists() });
      queryClient.invalidateQueries({ queryKey: notificationKeys.unreadCount() });
    },
  });
}

export function useDismissNotification() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient.post(`/notifications/${id}/dismiss`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: notificationKeys.lists() });
      queryClient.invalidateQueries({ queryKey: notificationKeys.unreadCount() });
    },
  });
}

export function useUpdateNotificationPreferences() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: Partial<NotificationPreferences>) => {
      const response = await apiClient.put<{ preferences: NotificationPreferences }>(
        '/notifications/preferences',
        data
      );
      return response.data.preferences;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: notificationKeys.preferences() });
    },
  });
}

export function useCreateNotification() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: Partial<Notification> & { user_ids?: string[] }) => {
      const response = await apiClient.post<{ notification: Notification }>(
        '/notifications',
        data
      );
      return response.data.notification;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: notificationKeys.lists() });
      queryClient.invalidateQueries({ queryKey: notificationKeys.unreadCount() });
    },
  });
}

// Hook for adaptive polling (faster when tab active)
export function useAdaptiveNotifications(params: NotificationListParams = {}) {
  const [isVisible, setIsVisible] = useState(true);
  const [isFocused, setIsFocused] = useState(true);

  // Track visibility and focus
  useEffect(() => {
    const handleVisibilityChange = () => {
      setIsVisible(document.visibilityState === 'visible');
    };

    const handleFocus = () => setIsFocused(true);
    const handleBlur = () => setIsFocused(false);

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);
    window.addEventListener('blur', handleBlur);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
      window.removeEventListener('blur', handleBlur);
    };
  }, []);

  // Determine polling interval based on visibility and focus
  const getPollingInterval = () => {
    if (!isVisible) return 60000; // 60s when tab hidden
    if (isFocused) return 5000;   // 5s when active and focused
    return 15000;                  // 15s when visible but not focused
  };

  const query = useQuery({
    queryKey: notificationKeys.list(params),
    queryFn: async () => {
      const searchParams = new URLSearchParams();

      if (params.status) searchParams.set('status', params.status);
      if (params.category) searchParams.set('category', params.category);
      if (params.limit) searchParams.set('limit', params.limit.toString());
      if (params.offset) searchParams.set('offset', params.offset.toString());
      if (params.unreadOnly) searchParams.set('unread_only', 'true');

      const response = await apiClient.get<NotificationListResponse>(
        `/notifications?${searchParams.toString()}`
      );
      return response.data;
    },
    refetchInterval: getPollingInterval(),
    staleTime: 3000,
  });

  return {
    ...query,
    isVisible,
    isFocused,
  };
}

// Hook for browser push notifications permission
export function usePushNotifications() {
  const [permission, setPermission] = useState<NotificationPermission>('default');
  const [isSupported, setIsSupported] = useState(false);

  useEffect(() => {
    setIsSupported('Notification' in window);
    if ('Notification' in window) {
      setPermission(Notification.permission);
    }
  }, []);

  const requestPermission = useCallback(async () => {
    if (!isSupported) return 'denied';

    const result = await Notification.requestPermission();
    setPermission(result);
    return result;
  }, [isSupported]);

  const showNotification = useCallback((title: string, options?: NotificationOptions) => {
    if (!isSupported || permission !== 'granted') return null;

    return new Notification(title, {
      icon: '/favicon.ico',
      badge: '/favicon.ico',
      ...options,
    });
  }, [isSupported, permission]);

  return {
    permission,
    isSupported,
    requestPermission,
    showNotification,
  };
}

// Hook for notification toasts with browser push support
interface NotificationToast {
  id: string;
  notification: Notification;
  createdAt: number;
}

export function useNotificationToasts() {
  const [toasts, setToasts] = useState<NotificationToast[]>([]);
  const previousUnreadRef = useRef<number>(0);
  const shownNotificationsRef = useRef<Set<string>>(new Set());
  const { data: unreadCount } = useUnreadCount();
  const { data: notificationsData } = useNotifications({ unreadOnly: true, limit: 5 });
  const { showNotification, permission } = usePushNotifications();

  // Show toast when new notifications arrive
  useEffect(() => {
    if (unreadCount !== undefined && unreadCount > previousUnreadRef.current) {
      // New notification arrived
      const newNotifications = notificationsData?.items.slice(0, unreadCount - previousUnreadRef.current) || [];

      newNotifications.forEach((notification) => {
        // Only show toast if not already shown
        if (!shownNotificationsRef.current.has(notification.id)) {
          shownNotificationsRef.current.add(notification.id);

          // Add to in-app toasts
          setToasts((prev) => [
            ...prev,
            { id: `toast-${notification.id}`, notification, createdAt: Date.now() },
          ]);

          // Show browser push notification if tab is not visible and permission granted
          if (document.hidden && permission === 'granted') {
            showNotification(notification.title, {
              body: notification.message,
              tag: notification.id,
              data: { url: notification.action_url },
            });
          }
        }
      });
    }
    previousUnreadRef.current = unreadCount || 0;
  }, [unreadCount, notificationsData, showNotification, permission]);

  // Auto-dismiss after 5 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      const now = Date.now();
      setToasts((prev) => prev.filter((t) => now - t.createdAt < 5000));
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  // Clean up shown notifications set periodically
  useEffect(() => {
    const cleanup = setInterval(() => {
      if (shownNotificationsRef.current.size > 100) {
        shownNotificationsRef.current.clear();
      }
    }, 60000);

    return () => clearInterval(cleanup);
  }, []);

  const dismissToast = useCallback((toastId: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== toastId));
  }, []);

  const dismissAll = useCallback(() => {
    setToasts([]);
  }, []);

  return {
    toasts,
    dismissToast,
    dismissAll,
  };
}
